// Desktop sign-in handoff helpers (C1249, TPT347) — pure, no DOM, no storage, so they are
// unit-testable under node. pages/login.js owns the state and the redirects; this module only
// parses/serializes the handoff blob the Task App (Electron) puts in the sign-in URL.
//
// The Task App opens `#/login?d=<base64url {port,nonce}>[&choose_account=1]` in the system
// browser. `d` identifies the loopback server waiting for the token; `choose_account=1` is
// sent by Project ▸ Re-authenticate / Change Account and asks the page to offer an account
// chooser instead of silently handing an already-signed-in browser session straight back.

const PORT_MIN = 1024;
const PORT_MAX = 65535;

// base64url blob → { port, nonce } or null. Validation mirrors the server's parseDesktopState()
// (api/src/routes/auth.js): the port ends up in a redirect to 127.0.0.1:<port>, so a malformed
// value is rejected here rather than trusted.
export function decodeHandoff(d) {
  try {
    const b64 = d.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const obj = JSON.parse(atob(b64 + pad));
    if (
      Number.isInteger(obj.port) && obj.port >= PORT_MIN && obj.port <= PORT_MAX &&
      typeof obj.nonce === 'string' && /^[a-f0-9]{1,64}$/i.test(obj.nonce)
    ) {
      return { port: obj.port, nonce: obj.nonce };
    }
  } catch { /* malformed — treat as no handoff */ }
  return null;
}

// Serializes ONLY { port, nonce } — extra handoff fields (chooseAccount) are page-local and are
// never round-tripped through Google's OAuth `state`.
export function encodeHandoff(handoff) {
  return btoa(JSON.stringify({ port: handoff.port, nonce: handoff.nonce }))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// window.location.hash → { port, nonce, chooseAccount } or null (no/invalid `d`).
export function readHandoffFromHash(hash) {
  const h = hash || '';
  const qIdx = h.indexOf('?');
  if (qIdx === -1) return null;
  const params = new URLSearchParams(h.slice(qIdx + 1));
  const d = params.get('d');
  const decoded = d ? decodeHandoff(d) : null;
  if (!decoded) return null;
  return { ...decoded, chooseAccount: params.get('choose_account') === '1' };
}

// The "Sign in with Google" href. In a chooser session the user explicitly wants another
// account, so Google is asked to show its own account picker (`select_account=1` →
// api/src/routes/auth.js buildGoogleAuthParams) rather than reusing the browser's Google session.
export function googleLoginHref(handoff) {
  const base = '/api/auth/google/login';
  if (!handoff) return base;
  return `${base}?desktop=${encodeHandoff(handoff)}${handoff.chooseAccount ? '&select_account=1' : ''}`;
}
